import { hrFormatDaysCount, hrFormatHours, hrFormatMinutes } from '@/modules/hr/components/hrCalendarUtils';
import { i18n } from '@/i18n';
import { hrOvertimeAbbrev, hrT } from '@/modules/hr/components/hrI18n';
import {
  hrDaysToMinutes,
  hrFormatWorkforceMetric,
  hrMinutesToDays,
  type HrTimeDisplayMode,
} from '@/modules/hr/components/hrTimeDisplayUtils';

/** Matches backend {@link BusinessActiveStatus#isBusinessActive}. */
export function hrIsBusinessActiveStatus(status?: string | null): boolean {
  if (!status?.trim()) {
    return true;
  }
  return status.trim().toUpperCase() === 'APPROVED';
}

export function hrNormalizeWorkflowStatus(status?: string | null): string {
  return (status ?? '').trim().toUpperCase();
}

/** Matches backend terminal workflow outcomes that must not appear as pending approval. */
export function hrIsTerminalWorkflowStatus(status?: string | null): boolean {
  const code = hrNormalizeWorkflowStatus(status);
  return code === 'REJECTED' || code === 'CANCELLED';
}

/** Matches backend {@link BusinessActiveStatus#isPendingApproval} plus other in-flight states. */
export function hrIsPendingWorkflowStatus(status?: string | null): boolean {
  const code = hrNormalizeWorkflowStatus(status);
  if (!code || hrIsBusinessActiveStatus(status) || hrIsTerminalWorkflowStatus(status)) {
    return false;
  }
  return true;
}

export function hrIsApprovedLeaveStatus(statusCode?: string | null): boolean {
  return (statusCode ?? '').trim().toUpperCase() === 'APPROVED';
}

/** Primary workflow code for a group of attendance punches on one calendar day. */
export function hrAttendanceWorkflowStatus(
  rows: Array<{ approvalStatus?: string | null }>,
): string {
  const pending = rows.find((row) => !hrIsBusinessActiveStatus(row.approvalStatus));
  if (pending) return pending.approvalStatus?.trim() || 'PENDING';
  return 'APPROVED';
}

export function hrPendingApprovalTrend(
  pendingHours?: number | null,
  label = 'pending approval',
): string | undefined {
  if (pendingHours == null || pendingHours <= 0) {
    return undefined;
  }
  return `${hrFormatHours(pendingHours)} ${label}`;
}

export function hrPendingAmountTrend(
  pendingAmount?: number | null,
  label = 'pending approval',
): string | undefined {
  if (pendingAmount == null || pendingAmount <= 0) {
    return undefined;
  }
  return `${pendingAmount.toFixed(2)} ${label}`;
}

export function hrPendingLeaveDaysTrend(
  pendingDays?: number | null,
  label = 'pending approval',
): string | undefined {
  if (pendingDays == null || pendingDays <= 0) {
    return undefined;
  }
  return `${hrFormatDaysCount(pendingDays)} ${label}`;
}

export function hrPendingOvertimeTrend(
  pendingMinutes?: number | null,
  label = 'pending approval',
): string | undefined {
  if (pendingMinutes == null || pendingMinutes <= 0) {
    return undefined;
  }
  return hrT('calendar.pendingOvertimeTrend', `${hrFormatMinutes(pendingMinutes)} OT ${label}`, {
    minutes: hrFormatMinutes(pendingMinutes),
    abbrev: hrOvertimeAbbrev(),
    label,
  });
}

export function hrPendingLeaveMinutesTrend(
  pendingMinutes?: number | null,
  label = 'pending approval',
): string | undefined {
  if (pendingMinutes == null || pendingMinutes <= 0) {
    return undefined;
  }
  return `${hrFormatMinutes(pendingMinutes)} ${label}`;
}

export type HrEmployeeMonthTotalsTooltipInput = {
  workedHours?: number;
  pendingWorkedHours?: number;
  leaveDays?: number;
  pendingLeaveDays?: number;
  leaveMinutes?: number;
  pendingLeaveMinutes?: number;
  otMinutes?: number;
  pendingOtMinutes?: number;
  showWorked?: boolean;
  showLeave?: boolean;
  showOvertime?: boolean;
  displayMode?: HrTimeDisplayMode;
};

function tooltipMetric(
  label: string,
  minutes: number,
  days: number,
  mode: HrTimeDisplayMode,
): string {
  const approved = hrFormatWorkforceMetric(minutes, days, mode);
  return `${label}: ${approved}`;
}

/** Month totals tooltip: approved summary plus pending breakdown on hover. */
export function hrEmployeeMonthTotalsTooltip(input: HrEmployeeMonthTotalsTooltipInput): string | undefined {
  const lines: string[] = [];
  const {
    workedHours = 0,
    pendingWorkedHours = 0,
    leaveDays = 0,
    pendingLeaveDays = 0,
    leaveMinutes = 0,
    pendingLeaveMinutes = 0,
    otMinutes = 0,
    pendingOtMinutes = 0,
    showWorked = true,
    showLeave = true,
    showOvertime = true,
    displayMode = 'BOTH',
  } = input;

  const workedMinutes = workedHours * 60;
  const pendingWorkedMinutes = pendingWorkedHours * 60;
  const resolvedLeaveMinutes =
    leaveMinutes > 0 ? leaveMinutes : hrDaysToMinutes(leaveDays);
  const resolvedPendingLeaveMinutes =
    pendingLeaveMinutes > 0 ? pendingLeaveMinutes : hrDaysToMinutes(pendingLeaveDays);

  if (showWorked) {
    if (workedMinutes > 0) {
      lines.push(
        tooltipMetric(
          i18n.isInitialized ? i18n.t('calendar.tooltip.workedApproved', { ns: 'hr' }) : 'Worked (approved)',
          workedMinutes,
          hrMinutesToDays(workedMinutes),
          displayMode,
        ),
      );
    }
    if (pendingWorkedMinutes > 0) {
      lines.push(
        tooltipMetric(
          i18n.isInitialized ? i18n.t('calendar.tooltip.workedPending', { ns: 'hr' }) : 'Worked (pending)',
          pendingWorkedMinutes,
          hrMinutesToDays(pendingWorkedMinutes),
          displayMode,
        ),
      );
    }
  }

  if (showLeave) {
    if (resolvedLeaveMinutes > 0 || leaveDays > 0) {
      lines.push(
        tooltipMetric(
          i18n.isInitialized ? i18n.t('calendar.tooltip.leaveApproved', { ns: 'hr' }) : 'Leave (approved)',
          resolvedLeaveMinutes,
          leaveDays,
          displayMode,
        ),
      );
    }
    if (resolvedPendingLeaveMinutes > 0 || pendingLeaveDays > 0) {
      lines.push(
        tooltipMetric(
          i18n.isInitialized ? i18n.t('calendar.tooltip.leavePending', { ns: 'hr' }) : 'Leave (pending)',
          resolvedPendingLeaveMinutes,
          pendingLeaveDays,
          displayMode,
        ),
      );
    }
  }

  if (showOvertime) {
    if (otMinutes > 0) {
      lines.push(
        tooltipMetric(
          i18n.isInitialized ? i18n.t('calendar.tooltip.overtimeApproved', { ns: 'hr' }) : 'OT (approved)',
          otMinutes,
          hrMinutesToDays(otMinutes),
          displayMode,
        ),
      );
    }
    if (pendingOtMinutes > 0) {
      lines.push(
        tooltipMetric(
          i18n.isInitialized ? i18n.t('calendar.tooltip.overtimePending', { ns: 'hr' }) : 'OT (pending)',
          pendingOtMinutes,
          hrMinutesToDays(pendingOtMinutes),
          displayMode,
        ),
      );
    }
  }

  return lines.length > 0 ? lines.join('\n') : undefined;
}
